import { useEffect, useState } from 'react';
import { useDebounceCallback } from 'src/hooks/useDebounceCallback';
import { IUserImageModel } from 'src/store/_models/userImage';
import { IAdjustments } from './Adjustments.types';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { parseAnalyticsParamsByUserImage } from 'src/_shared/analytics.utils';

const parseAdjustments = ({ brightness, contrast, saturation }: IAdjustments) => ({
  brightness: parseInt(brightness.toFixed(0)),
  contrast: parseInt(contrast.toFixed(0)),
  saturation: parseInt(saturation.toFixed(0)),
});

export const useAdjustmentsLogic = (userImage?: IUserImageModel) => {
  const [adjustments, _setAdjustments] = useState<IAdjustments>({
    brightness: userImage?.brightness ?? 0,
    contrast: userImage?.contrast ?? 0,
    saturation: userImage?.saturation ?? 0,
  });

  useEffect(()=>{
    updateAdjustments({
      brightness: userImage?.brightness ?? 0,
      contrast: userImage?.contrast ?? 0,
      saturation: userImage?.saturation ?? 0, 
    });
  },[ userImage?.brightness,  userImage?.contrast, userImage?.saturation])

  const debounceUpdateAdjustments = useDebounceCallback((updateMethod, newAdjustments: IAdjustments) => {
    return updateMethod && updateMethod(newAdjustments);
  }, 10);

  const updateAdjustments = (newAdjustments: IAdjustments) => {
    const newAdjustmentsWithOnlyIntegers = parseAdjustments(newAdjustments);
    _setAdjustments(newAdjustmentsWithOnlyIntegers);
    debounceUpdateAdjustments(userImage?.generateAdjustedImage, newAdjustmentsWithOnlyIntegers);
  };

  const setBrightness = (brightness: number) => {
    updateAdjustments({ ...adjustments, brightness });
  };

  const setContrast = (contrast: number) => {
    updateAdjustments({ ...adjustments, contrast });
  };

  const setSaturation = (saturation: number) => {
    updateAdjustments({ ...adjustments, saturation });
  };

  const setAdjustments = (newAdjustments: IAdjustments) => {
    const newAdjustmentsWithOnlyIntegers = parseAdjustments(newAdjustments);
    _setAdjustments(newAdjustmentsWithOnlyIntegers);
    debounceUpdateAdjustments(userImage?.setAdjustments, newAdjustmentsWithOnlyIntegers);
  };

  const onPressRestore = () => {
    setAdjustments({
      brightness: 0,
      contrast: 0,
      saturation: 0,
    });
    amplitude.trackEvent(AnalyticsEvents.modifyPhoto.adjustRestoreClick, parseAnalyticsParamsByUserImage(userImage));
  };

  return {
    userImage,
    brightness: adjustments.brightness,
    contrast: adjustments.contrast,
    saturation: adjustments.saturation,
    setBrightness,
    setContrast,
    setSaturation,
    onPressRestore,
    setAdjustments,
  };
};
